/**
 * Per-panel-kind empty-paragraph placeholder.
 *
 * Renders "Type / for commands or start writing…" (i18n key
 * `wiki.panel.placeholder`) inside the FIRST child paragraph of an
 * empty panel when the selection sits inside that panel. Mirrors the
 * shape of the global `Placeholder` extension TipTap ships — the
 * official extension only fires on the document's first paragraph, so
 * we add a parallel plugin scoped to each panel's body.
 *
 * The decoration is a single inline widget at the start of the
 * paragraph, styled via CSS (`.ProseMirror p[data-panel-placeholder]`
 * + a matching pseudo-element in `src/styles/global.css`). Using a
 * data-attribute + CSS keeps the rendered HTML stable (no inline
 * `<span>` that would leak into the document JSON) and lets the same
 * pseudo-element machinery the existing placeholder uses handle the
 * dark / pseudo-locale variants.
 */

import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'

import { i18n } from '@/i18n'

export interface PanelPlaceholderPluginOptions {
  /** ProseMirror node name to scope this placeholder to. */
  panelNodeName: string
  /**
   * i18n key for the placeholder copy. The shared default is
   * `wiki.panel.placeholder` — overridable so future per-kind hints
   * (e.g. "Tip: type / for a panel-aware command") can land without
   * changing this plugin.
   */
  translationKey: string
}

/**
 * Stable plugin-key per panel node name. We can't share one key across
 * the 5 kinds — ProseMirror requires plugins to have unique keys, and
 * each panel kind registers its own factory-built plugin.
 */
function panelPlaceholderKey(panelNodeName: string): PluginKey {
  return new PluginKey(`panel-placeholder-${panelNodeName}`)
}

export function buildPanelPlaceholderPlugin({
  panelNodeName,
  translationKey,
}: PanelPlaceholderPluginOptions) {
  const key = panelPlaceholderKey(panelNodeName)

  return new Plugin({
    key,
    props: {
      decorations(state) {
        const { doc, selection } = state
        const { $from } = selection

        // Walk up from the cursor's position looking for a panel of
        // this kind. If we find one, check whether its body is empty
        // (first child paragraph, no text). The plugin is scoped per
        // panel name so each kind runs its own walk; that's fine —
        // the cost is one ancestor walk per render frame.
        let panel: { pos: number; size: number } | null = null
        for (let d = $from.depth; d > 0; d--) {
          const ancestor = $from.node(d)
          if (ancestor.type.name === panelNodeName) {
            panel = { pos: $from.before(d), size: ancestor.nodeSize }
            break
          }
        }
        if (!panel) return DecorationSet.empty

        const panelNode = doc.nodeAt(panel.pos)
        if (!panelNode) return DecorationSet.empty

        // The panel's first child is the paragraph we decorate. If
        // there are multiple children OR the first child has any
        // content, the panel isn't "empty" and we skip the
        // placeholder.
        if (panelNode.childCount !== 1) return DecorationSet.empty
        const firstChild = panelNode.firstChild
        if (!firstChild || firstChild.type.name !== 'paragraph') return DecorationSet.empty
        if (firstChild.content.size !== 0) return DecorationSet.empty

        // Position of the first child paragraph within the doc =
        // panel.pos + 1 (step inside the panel's open token).
        const paragraphPos = panel.pos + 1
        const placeholderText = i18n.t(translationKey, { defaultValue: '' })
        if (typeof placeholderText !== 'string' || placeholderText.length === 0) {
          return DecorationSet.empty
        }

        const deco = Decoration.node(paragraphPos, paragraphPos + firstChild.nodeSize, {
          // The CSS rule on `[data-panel-placeholder]` reads the text
          // via `content: attr(data-panel-placeholder-text)` so we
          // route through an attribute (no inline-span injection).
          'data-panel-placeholder': '',
          'data-panel-placeholder-text': placeholderText,
          class: 'is-panel-empty',
        })

        return DecorationSet.create(doc, [deco])
      },
    },
  })
}
